import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Warning, CircleNotch } from '@phosphor-icons/react'
import { userApi } from '@/api/users'
import type { OrgUserDetail } from '@/api/users'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface UserDangerZoneProps {
  user: OrgUserDetail
  orgId: string
  onAction: () => void
}

export function UserDangerZone({ user, orgId, onAction }: UserDangerZoneProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Deactivation state
  const [showDeactivate, setShowDeactivate] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState('')

  // Reactivation state
  const [showReactivate, setShowReactivate] = useState(false)
  const [reactivateRole, setReactivateRole] = useState('')

  // Fetch roles for reactivation
  const { data: roles } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: () => userApi.listRoles(orgId),
    enabled: showReactivate && !!orgId,
  })

  const deactivateMutation = useMutation({
    mutationFn: () => userApi.deactivate(orgId, user.id),
    onSuccess: () => {
      toast({
        title: 'User deactivated',
        description: `${user.name} has been deactivated and can no longer log in.`,
      })
      queryClient.invalidateQueries({ queryKey: ['user', orgId, user.id] })
      queryClient.invalidateQueries({ queryKey: ['users', orgId] })
      queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', orgId] })
      setShowDeactivate(false)
      setConfirmEmail('')
      onAction()
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to deactivate user',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: () => userApi.reactivate(orgId, user.id, { role_slug: reactivateRole }),
    onSuccess: () => {
      toast({
        title: 'User reactivated',
        description: `${user.name} has been reactivated and can log in again.`,
      })
      queryClient.invalidateQueries({ queryKey: ['user', orgId, user.id] })
      queryClient.invalidateQueries({ queryKey: ['users', orgId] })
      queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', orgId] })
      setShowReactivate(false)
      setReactivateRole('')
      onAction()
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reactivate user',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  function handleDeactivateOpen() {
    setConfirmEmail('')
    setShowDeactivate(true)
  }

  function handleReactivateOpen() {
    setReactivateRole('')
    setShowReactivate(true)
  }

  const emailConfirmed = confirmEmail === user.email

  if (user.is_active) {
    return (
      <>
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-destructive">
              <Warning className="h-5 w-5" weight="fill" />
              Deactivate User
            </CardTitle>
            <CardDescription>
              This will prevent {user.name} from logging in. Their role assignment will be removed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={handleDeactivateOpen}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending && (
                <CircleNotch className="h-4 w-4 animate-spin mr-1.5" />
              )}
              Deactivate User...
            </Button>
          </CardContent>
        </Card>

        <AlertDialog open={showDeactivate} onOpenChange={setShowDeactivate}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate {user.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This action will prevent the user from logging in and remove their role assignment.
                Type the user's email to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-2 py-2">
              <Label htmlFor="confirm-email" className="text-sm text-muted-foreground">
                Type <span className="font-mono font-medium text-foreground">{user.email}</span> to confirm
              </Label>
              <Input
                id="confirm-email"
                type="text"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user.email}
                autoComplete="off"
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmEmail('')}>Cancel</AlertDialogCancel>
              <Button
                variant="destructive"
                onClick={() => deactivateMutation.mutate()}
                disabled={!emailConfirmed || deactivateMutation.isPending}
              >
                {deactivateMutation.isPending && (
                  <CircleNotch className="h-4 w-4 animate-spin mr-1.5" />
                )}
                Deactivate User
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    )
  }

  // Inactive user -- show reactivation
  return (
    <>
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-lg">Reactivate User</CardTitle>
          <CardDescription>
            Restore access for {user.name}. You must assign a role.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleReactivateOpen}
            disabled={reactivateMutation.isPending}
          >
            {reactivateMutation.isPending && (
              <CircleNotch className="h-4 w-4 animate-spin mr-1.5" />
            )}
            Reactivate User...
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showReactivate} onOpenChange={setShowReactivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate {user.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Select a role to assign. The user will be able to log in again.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            <Label>Role</Label>
            <Select value={reactivateRole} onValueChange={setReactivateRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles?.map((role) => (
                  <SelectItem key={role.slug} value={role.slug}>
                    {role.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReactivateRole('')}>Cancel</AlertDialogCancel>
            <Button
              onClick={() => reactivateMutation.mutate()}
              disabled={!reactivateRole || reactivateMutation.isPending}
            >
              {reactivateMutation.isPending && (
                <CircleNotch className="h-4 w-4 animate-spin mr-1.5" />
              )}
              Reactivate User
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
